import { Box, Grid, useMediaQuery } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { SnackbarProvider } from "notistack";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import store from "../src/redux/store/store";
import BulkOrderError from "./components/Alert/BulkOrderError";
import BulkOrderSuccess from "./components/Alert/BulkOrderSuccess";
import ErrorAlert from "./components/Alert/ErrorAlert";
import ErrorMessage from "./components/Alert/ErrorMessage";
import SuccessAlert from "./components/Alert/SuccessAlert";
import BackDropComponent from "./components/BackDrop";
import ErrorBoundary from "./components/ErrorBoundary";
import Header from "./containers/layout/Header";
import NavBar from "./containers/layout/NavBar";
import "./containers/layout/assets/layout.scss";
import FinalChargesPage from "./containers/pages/AdminPanel/FinalCharges";
import FinancePage from "./containers/pages/AdminPanel/Finance";
import InvoicingPage from "./containers/pages/AdminPanel/InvoicingPage";
import RemittanceApprovalsPage from "./containers/pages/AdminPanel/RemittanceApprovals";
import BulkActivity from "./containers/pages/BulkActivity/BulkActivity";
import { logout, refreshToken } from "./containers/pages/OnBoarding/Login/LoginService";
import RateCardLandingPage from "./containers/pages/RateCard";
import BulkWeightDisputeDetails from "./containers/pages/ShipmentDisputes/BulkWeightDispute";
import DisputeDiscripancyData from "./containers/pages/ShipmentDisputes/DisputeDiscripancy";
import TenantManagerDetails from "./containers/pages/TenantManager/TenantManagerDetails";
import VasControls from "./containers/pages/VasControl/VasControl";
import { config, getApiUrl } from "./config";
import { ProjectThemeProvider } from "./components/ProjectThemeProvider";
import B2BMIS from "./containers/pages/B2BMIS/B2BMis";
import { jwtDecode } from "jwt-decode";
const OBLogin = lazy(() => import("./containers/pages/OnBoarding/Login"));
const RateManagement = lazy(() => import("./containers/pages/RateManangement"));
const RatePlayground = lazy(() => import("./containers/pages/RatePlayground"));
// Create dynamic theme based on project
const createDynamicTheme = () => {
    const primaryColor = config.theme.primaryColor;
    return createTheme({
        palette: {
            primary: {
                main: primaryColor,
            },
        },
        components: {
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        "& .MuiLinearProgress-bar": {
                            backgroundColor: `${primaryColor} !important`,
                        },
                    },
                },
            },
            // Add TextField styling
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                borderColor: "#000000",
                            },
                            "&:hover fieldset": {
                                borderColor: primaryColor,
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                            },
                        },
                    },
                },
            },
            // Add Select styling
            MuiSelect: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000000",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000000",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: `${primaryColor} !important`,
                        },
                    },
                },
            },
            // Add Button styling
            MuiButton: {
                styleOverrides: {
                    contained: {
                        backgroundColor: primaryColor,
                        "&:hover": {
                            backgroundColor: primaryColor,
                            opacity: 0.9,
                        },
                    },
                },
            },
            // Add OutlinedInput styling
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000000",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000000",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: `${primaryColor} !important`,
                        },
                    },
                },
            },
        },
        typography: {
            fontFamily: ["Poppins"].join(","),
        },
    });
};
const queryClient = new QueryClient();
const App = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [width, setWidth] = useState(52);
    const [isAuthenticated, setIsAuthenticated] = useState(true);
    const [hideNavigation, setHideNavigation] = useState(false);
    const theme = createDynamicTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const handleChildValueChange = (value) => {
        setIsDrawerOpen(value);
        setWidth(value ? 280 : 52);
    };
    // Check local storage for authentication token
    const checkIsUserAuthenticated = () => {
        const updatedToken = localStorage.getItem("accessToken");
        if (updatedToken) {
            const decodedToken = jwtDecode(updatedToken);
            if (decodedToken === null || decodedToken === void 0 ? void 0 : decodedToken.ta_state) {
                refreshToken();
                setIsAuthenticated(!!updatedToken);
            }
            else {
                logout();
                window.location.pathname = "/login";
            }
        }
        else {
            if (window.location.pathname !== "/login")
                window.location.pathname = "/login";
        }
        let url = window.location.pathname;
        if (["/login", "/onboard"].find((x) => url.startsWith(x)) || url === "/") {
            setHideNavigation(false);
            return;
        }
        else if (!!updatedToken) {
            setHideNavigation(true);
        }
        else {
            setHideNavigation(false);
        }
    };
    useEffect(() => {
        // Check local storage for authentication token
        checkIsUserAuthenticated();
        window.addEventListener("storageAccessTokenChange", () => {
            checkIsUserAuthenticated();
        });
        window.onerror = function (message, source, lineno, colno, error) {
            // Log the error or handle it in your preferred way
            console.error("Global error:", message, source, lineno, colno, error);
        };
        // Log current project configuration
        console.log(`Running: ${config.project} (${config.env})`);
        console.log(`API URL: ${getApiUrl()}`);
    }, []);
    return (React.createElement(Provider, { store: store },
        React.createElement(ProjectThemeProvider, null,
            React.createElement(SnackbarProvider, { anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                }, Components: {
                    success: SuccessAlert,
                    error: ErrorAlert,
                    BulkOrderError: BulkOrderError,
                    BulkOrderSuccess: BulkOrderSuccess,
                    ErrorMessage: ErrorMessage,
                } },
                React.createElement(ErrorBoundary, null,
                    React.createElement(QueryClientProvider, { client: queryClient },
                        React.createElement(ThemeProvider, { theme: theme },
                            React.createElement(BrowserRouter, null,
                                React.createElement(Box, null,
                                    React.createElement(Grid, { container: true, direction: "row" },
                                        !isMobile && hideNavigation && React.createElement(NavBar, { handleHover: handleChildValueChange, isDrawerOpen: isDrawerOpen }),
                                        React.createElement(Grid, { style: isMobile || !hideNavigation ? { width: "100%" } : { width: `calc(100% - 52px)`, marginLeft: "52px" } },
                                            hideNavigation && (React.createElement(React.Fragment, null,
                                                React.createElement(Header, { setIsAuthenticated: setIsAuthenticated }))),
                                            React.createElement(Suspense, { fallback: React.createElement(BackDropComponent, { open: true }) },
                                                React.createElement(Routes, null,
                                                    isAuthenticated && (React.createElement(React.Fragment, null,
                                                        React.createElement(Route, { path: "/invoice", element: React.createElement(InvoicingPage, null) }),
                                                        React.createElement(Route, { path: "/charges", element: React.createElement(FinalChargesPage, null) }),
                                                        React.createElement(Route, { path: "/finance", element: React.createElement(FinancePage, null) }),
                                                        React.createElement(Route, { path: "/remittance-approvals", element: React.createElement(RemittanceApprovalsPage, null) }),
                                                        React.createElement(Route, { path: "/tenantmanager", element: React.createElement(TenantManagerDetails, null) }),
                                                        React.createElement(Route, { path: "/bulkactivity", element: React.createElement(BulkActivity, null) }),
                                                        React.createElement(Route, { path: "/rate-management/:tabStatus", element: React.createElement(RateManagement, null) }),
                                                        React.createElement(Route, { path: "/rate-playground", element: React.createElement(RatePlayground, null) }),
                                                        React.createElement(Route, { path: "/vas-control", element: React.createElement(VasControls, null) }),
                                                        React.createElement(Route, { path: "/weightdispute", element: React.createElement(DisputeDiscripancyData, null) }),
                                                        React.createElement(Route, { path: "/weightdispute/:orderId", element: React.createElement(DisputeDiscripancyData, null) }),
                                                        React.createElement(Route, { path: "/weightdispute/bulk", element: React.createElement(BulkWeightDisputeDetails, null) }),
                                                        React.createElement(Route, { path: "/ratecard", element: React.createElement(RateCardLandingPage, null) }),
                                                        React.createElement(Route, { path: "/mis", element: React.createElement(B2BMIS, null) }))),
                                                    React.createElement(Route, { path: "/login", element: React.createElement(OBLogin, null) }),
                                                    React.createElement(Route, { path: "/", element: React.createElement(OBLogin, null) }),
                                                    React.createElement(Route, { path: "*", element: React.createElement(OBLogin, null) }))))))))))))));
};
export default App;
